import { api } from "@/api/axios.ts";
import {
  ApiBlogChannel,
  ApiBlogChannelFilter,
  ApiBlogChannelSave,
  ApiBlogChannelShort,
  ApiBlogCommentSave,
  ApiBlogMyChannelsUnreadCount,
  ApiBlogPost,
  ApiBlogPostCommentsSearchFilter,
  ApiBlogPostSave,
  ApiBlogPostShort,
  ApiBlogPostsSearchFilter,
  ApiBlogPostViewer,
  ApiBlogPostViewersFilter,
  ApiBlogReactionsSearchFilter,
} from "@/types/blog.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiOperationResult } from "@/types/common.types.ts";
import { ApiFileLink } from "@/types/file.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";

export const apiBlog = {
  searchPosts: async function (filter: ApiBlogPostsSearchFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiBlogPostShort>>("/api/v1/blog/posts/search", filter, signal);
  },
  getPostById: async function (id: number) {
    return api.get<ApiBlogPost>(`/api/v1/blog/posts/${id}`);
  },
  createPost: async function (payload: ApiBlogPostSave) {
    return api.post<ApiBlogPostShort>("/api/v1/blog/posts", payload);
  },
  updatePost: async function (id: number, payload: ApiBlogPostSave) {
    return api.put<ApiBlogPostShort>(`/api/v1/blog/posts/${id}`, payload);
  },
  deletePost: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/blog/posts/${id}`);
  },
  getAttachmentLink: async function (postId: number, fileUuid: string) {
    return api.get<ApiFileLink>(`/api/v1/blog/posts/${postId}/attachments/${fileUuid}/link`);
  },
  deleteAttachment: async function (fileUuid: string) {
    return api.delete<ApiOperationResult>(`/api/v1/blog/attachments/${fileUuid}`);
  },
  searchComments: async function (postId: number, filter: ApiBlogPostCommentsSearchFilter) {
    return api.post<ApiPagedData<ApiComment>>(
      `/api/v1/blog/posts/${postId}/comments/search`,
      filter
    );
  },
  readComments: async function (postId: number, comment_ids: number[]) {
    return api.post<ApiComment>(`/api/v1/blog/posts/${postId}/comments/read`, { comment_ids });
  },
  addComment: async function (postId: number, payload: ApiBlogCommentSave) {
    return api.post<ApiComment>(`/api/v1/blog/posts/${postId}/comments`, payload);
  },
  deleteComment: async function (postId: number, commentId: number) {
    return api.delete<ApiOperationResult>(`/api/v1/blog/posts/${postId}/comments/${commentId}`);
  },
  updateComment: async function (postId: number, commentId: number, payload: ApiBlogCommentSave) {
    return api.put<ApiComment>(`/api/v1/blog/posts/${postId}/comments/${commentId}`, payload);
  },
  searchReactions: async function (filter: ApiBlogReactionsSearchFilter) {
    return api.post<ApiEmployeeShort[]>(
      `/api/v1/blog/posts/${filter.post_id}/reactions/employees-search`,
      filter
    );
  },
  addReaction: async function (id: number, emoji: string) {
    return api.post<ApiOperationResult>(`/api/v1/blog/posts/${id}/reactions`, { emoji });
  },
  deleteReaction: async function (id: number, emoji: string) {
    return api.delete<ApiOperationResult>(`/api/v1/blog/posts/${id}/reactions`, { emoji });
  },

  getMyChannels: async function (filter: ApiBlogChannelFilter) {
    return api.get<ApiBlogChannelShort[]>(`/api/v1/blog/channels/my`, filter);
  },
  getMyChannelsUnreadCount: async function () {
    return api.get<ApiBlogMyChannelsUnreadCount>(`/api/v1/blog/channels/my/unread-count`);
  },
  updateMyChannels: async function (channel_ids: number[]) {
    return api.put<ApiBlogChannelShort[]>(`/api/v1/blog/channels/my`, { channel_ids });
  },
  getMyChannelById: async function (id: number) {
    return api.get<ApiBlogChannel>(`/api/v1/blog/channels/my/${id}`);
  },
  createChannel: async function (payload: ApiBlogChannelSave) {
    return api.post<ApiBlogChannelShort>("/api/v1/blog/channels", payload);
  },
  updateChannel: async function (id: number, payload: ApiBlogChannelSave) {
    return api.put<ApiBlogChannelShort>(`/api/v1/blog/channels/${id}`, payload);
  },
  deleteChannel: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/blog/channels/${id}`);
  },
  getPostViewers: async function (filter: ApiBlogPostViewersFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiBlogPostViewer>>(
      `/api/v1/blog/posts/views/employees-search`,
      filter,
      signal
    );
  },
};
